<template>
  <section ref="containerRef" class="journey-v2" tabindex="0" @keydown="handleKeydown">
    <div ref="headerRef" class="journey-v2__header">
      <div class="journey-v2__header-left">
        <span class="journey-v2__label">{{ $t('page.journeyV2.label') }}</span>
        <h2 class="journey-v2__title">{{ $t('page.journeyV2.title') }}</h2>
      </div>

      <a
        href="https://linkedin.com/in/juanlias"
        target="_blank"
        rel="noopener noreferrer"
        class="journey-v2__social-link"
        aria-label="Visit LinkedIn Profile"
      >
        <div class="journey-v2__social-icon">
          <Linkedin01Icon />
        </div>
        <span class="hidden md:inline">{{ $t('page.journeyV2.connect') }}</span>
      </a>
    </div>

    <div class="journey-v2__accordion">
      <div
        v-for="(item, index) in journeyData"
        :key="item.id"
        :ref="(el) => (cardRefs[index] = el)"
        @click="setActiveIndex(index)"
        @mousemove="handleCardMouseMove($event, index)"
        @mouseleave="handleCardMouseLeave(index)"
        class="journey-v2__card"
        :class="{ 'journey-v2__card--active': activeIndex === index }"
        :style="{ '--card-flex': activeIndex === index ? 4 : 1 }"
      >
        <div
          class="journey-v2__card-bg"
          :class="[
            `journey-v2__card-bg--${index}`,
            { 'journey-v2__card-bg--active': activeIndex === index },
          ]"
        >
          <!-- Border Glow -->
          <div class="journey-v2__border-glow"></div>
          
          <!-- Internal gradients only, no orb inside -->
          <!-- <div class="journey-v2__gradient journey-v2__gradient--base"></div> -->
        </div>

        <div
          :ref="(el) => setVerticalRef(el, index)"
          class="journey-v2__vertical"
          :class="{ 'opacity-0': activeIndex === index }"
        >
          <div class="journey-v2__vertical-desktop">
            <span class="journey-v2__vertical-year">{{ item.startYear }}</span>
            <span class="journey-v2__vertical-line"></span>
            <span class="journey-v2__vertical-role">{{ item.role }}</span>
          </div>
          <div class="journey-v2__vertical-mobile">
            <span class="journey-v2__vertical-year-mobile">{{ item.startYear }}</span>
            <span class="journey-v2__vertical-role-mobile">{{ item.role }}</span>
          </div>
        </div>

        <div
          :ref="(el) => setContentRef(el, index)"
          class="journey-v2__content"
          v-show="activeIndex === index"
        >
          <div class="journey-v2__info">
            <div class="journey-v2__meta animate-child">
              <div class="journey-v2__meta-date">
                <CalendarFavorite01Icon />
                <span>{{ item.period }}</span>
                <Infinity01Icon v-if="item.period.includes('2016 -')" class="-ml-1" />
              </div>
              <div class="journey-v2__meta-separator"></div>
              <div class="journey-v2__meta-location">
                <LocationStar01Icon />
                <span>{{ item.location }}</span>
              </div>
            </div>

            <div class="journey-v2__title-block animate-child">
              <h3 class="journey-v2__company-name">{{ item.company }}</h3>

              <p v-if="!item.roles" class="journey-v2__role">{{ item.role }}</p>
              <div v-if="item.roles" class="journey-v2__roles-timeline">
                <div
                  v-for="(subRole, rIndex) in item.roles"
                  :key="rIndex"
                  class="journey-v2__sub-role"
                >
                  <div class="journey-v2__sub-role-spine">
                    <div class="journey-v2__sub-role-dot"></div>
                    <div
                      v-if="rIndex < item.roles.length - 1"
                      class="journey-v2__sub-role-line"
                    ></div>
                  </div>
                  <div class="journey-v2__sub-role-content">
                    <h4 class="journey-v2__sub-role-title">{{ subRole.title }}</h4>
                    <div class="journey-v2__sub-role-meta">
                      <span>{{ subRole.period }}</span>
                      <span v-if="subRole.type" class="journey-v2__sub-role-type"
                        >• {{ subRole.type }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="journey-v2__description-block animate-child">
              <p class="journey-v2__description">{{ item.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div ref="timelineRef" class="timeline-footer">
      <template v-for="(year, index) in years" :key="year">
        <div class="timeline-footer__item">
          <div
            class="timeline-footer__dot"
            :class="[
              year >= selectedJob.startYear && year <= selectedJob.endYear
                ? 'timeline-footer__dot--active'
                : 'timeline-footer__dot--inactive',
            ]"
            :data-year="year"
          ></div>
          <span
            class="timeline-footer__label"
            :class="
              year >= selectedJob.startYear && year <= selectedJob.endYear
                ? 'timeline-footer__label--active'
                : 'timeline-footer__label--inactive'
            "
          >
            {{ year }}
          </span>
        </div>

        <div
          v-if="index < years.length - 1"
          class="timeline-footer__line-wrapper"
          :data-year="year"
        >
          <div class="timeline-footer__line-base"></div>
          <div
            :ref="(el) => setLineRef(el, index)"
            class="timeline-footer__line-active"
            :class="{
              'is-active': year >= selectedJob.startYear && year < selectedJob.endYear,
            }"
          ></div>
        </div>
      </template>
    </div>

    <div class="journey-v2__keyboard-hint">
      <span>Use</span>
      <kbd>←</kbd>
      <kbd>→</kbd>
      <span>to navigate</span>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import gsap from 'gsap'
// import journeyData from '@/data/journey.json' // Removed in favor of i18n
import {
  CalendarFavorite01Icon,
  Infinity01Icon,
  Linkedin01Icon,
  LocationStar01Icon,
} from 'hugeicons-vue'
import { useI18n } from 'vue-i18n'
import { useDevice } from '@/composables/useDevice'

const { t, tm } = useI18n()

// Fetch journey data from i18n
const journeyData = computed(() => tm('page.journeyV2.items'))

const years = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025]

const containerRef = ref(null)
const headerRef = ref(null)
const timelineRef = ref(null)
const activeIndex = ref(0)

// Refs para elementos animados
const verticalRefs = ref({})
const contentRefs = ref({})
const lineRefs = ref({})
const cardRefs = ref({})
const { isDesktop } = useDevice()

// Store active GSAP context for cleanup
let activeGsapCtx = null
let activeTimeline = null
let scrollAnimationFrame = null

// Animation logic for header
const animateHeader = (element) => {
  if (!element) return

  const rect = element.getBoundingClientRect()
  const windowHeight = window.innerHeight

  // Zona de entrada (desde abajo) - más rápida para aparecer
  const enterStart = windowHeight * 1.1
  const enterEnd = windowHeight * 0.85

  // Zona de salida (por arriba) - solo cuando casi sale
  const exitStart = windowHeight * 0.15
  const exitEnd = -rect.height * 0.5

  let opacity = 1
  let blur = 0
  let y = 0

  // Entrando desde abajo
  if (rect.top > enterEnd) {
    const progress = Math.min(1, Math.max(0, (enterStart - rect.top) / (enterStart - enterEnd)))
    opacity = progress
    blur = (1 - progress) * 8
    y = (1 - progress) * 30
  }
  // Saliendo por arriba
  else if (rect.top < exitStart) {
    const progress = Math.min(1, Math.max(0, (exitStart - rect.top) / (exitStart - exitEnd)))
    opacity = Math.max(0.05, 1 - progress * 0.95)
    y = progress * -20
  }

  gsap.set(element, {
    opacity,
    filter: `blur(${blur}px)`,
    y,
    force3D: true,
  })
}

const handleScrollAnimation = () => {
  animateHeader(headerRef.value)
  scrollAnimationFrame = requestAnimationFrame(handleScrollAnimation)
}

const setVerticalRef = (el, index) => {
  if (el) verticalRefs.value[index] = el
}

const setContentRef = (el, index) => {
  if (el) contentRefs.value[index] = el
}

const setLineRef = (el, index) => {
  if (el) lineRefs.value[index] = el
}

// Computed para el trabajo seleccionado
const selectedJob = computed(() => {
  return journeyData.value[activeIndex.value]
})

const setActiveIndex = (index) => {
  if (activeIndex.value === index) return
  activeIndex.value = index
  
  if (!isDesktop.value) {
    nextTick(() => {
      const el = cardRefs.value[index]
      if (el) {
        // Small delay to allow expansion start if needed, but immediate feels more responsive
        // Using center block to focus the user on the content
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
  }
}

const handleCardMouseMove = (e, index) => {
  if (!isDesktop.value) return
  const card = cardRefs.value[index]
  if (!card) return

  const rect = card.getBoundingClientRect()
  const x = e.clientX - rect.left
  const y = e.clientY - rect.top

  card.style.setProperty('--orb-x', `${x}px`)
  card.style.setProperty('--orb-y', `${y}px`)
}

const handleCardMouseLeave = (index) => {
  if (!isDesktop.value) return
  // Optional: reset or fade out logic is handled by CSS opacity
}

// Keyboard navigation
const handleKeydown = (e) => {
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    e.preventDefault()
    const nextIndex = (activeIndex.value + 1) % journeyData.value.length
    setActiveIndex(nextIndex)
  } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    e.preventDefault()
    const prevIndex = (activeIndex.value - 1 + journeyData.value.length) % journeyData.value.length
    setActiveIndex(prevIndex)
  }
}

// Animación del timeline
const animateTimeline = (newIndex) => {
  const job = journeyData.value[newIndex]
  if (!job || !timelineRef.value) return

  // Kill previous timeline animation
  if (activeTimeline) {
    activeTimeline.kill()
  }

  const dots = timelineRef.value.querySelectorAll('.timeline-footer__dot')
  const labels = timelineRef.value.querySelectorAll('.timeline-footer__label')

  // Crear timeline secuencial
  const tl = gsap.timeline()
  activeTimeline = tl

  // PASO 1: Desactivar TODOS los elementos primero
  tl.to(
    dots,
    {
      scale: 1,
      duration: 0.2,
      ease: 'power2.in',
    },
    0
  )

  // Reset líneas activas
  Object.values(lineRefs.value).forEach((line) => {
    if (line) {
      gsap.to(line, { scaleX: 0, duration: 0.2, ease: 'power2.in' })
    }
  })

  // Pequeña pausa después de desactivar
  tl.to({}, { duration: 0.1 })

  // PASO 2: Encontrar el índice de inicio y fin
  const startIndex = years.indexOf(job.startYear)
  const endIndex = years.indexOf(job.endYear)

  // PASO 3: Animar secuencialmente desde startYear hasta endYear
  for (let i = startIndex; i <= endIndex; i++) {
    const dot = dots[i]
    const label = labels[i]
    const activeLine = lineRefs.value[i]

    // Animar el dot
    if (dot) {
      tl.to(
        dot,
        {
          scale: 1.3,
          duration: 0.15,
          ease: 'back.out(2)',
        },
        i === startIndex ? '>' : '-=0.12'
      )
    }

    // Animar el label (sin salto, solo asegurar estado si es necesario, pero CSS lo maneja)
    // Eliminamos el salto (bounce) solicitado por el usuario

    // Si hay línea hacia el siguiente año activo, animarla rapidamante
    if (activeLine && i < endIndex) {
      tl.fromTo(
        activeLine,
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 0.15,
          ease: 'none', // Linear para que fluya mejor entre puntos
        },
        '-=0.1'
      )
    }
  }
}

// Watch para animar cuando cambia el índice activo
watch(activeIndex, (newIndex, oldIndex) => {
  if (!containerRef.value) return

  // Kill previous context to prevent animation buildup
  if (activeGsapCtx) {
    activeGsapCtx.revert()
  }

  // Animar timeline
  animateTimeline(newIndex)

  // Wait for Vue to update DOM (v-show)
  nextTick(() => {
    const ctx = gsap.context(() => {
      const activeContent = contentRefs.value[newIndex]

      // Animate content IN with a delay if it exists
      if (activeContent) {
        // Reset state for animation
        gsap.set(activeContent, { opacity: 0 })

        // Animate container opacity
        gsap.to(activeContent, { opacity: 1, duration: 0.5, delay: 0.3 })

        // Animate children elements
        const animateChildren = activeContent.querySelectorAll('.animate-child')
        gsap.fromTo(
          animateChildren,
          { y: 15, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.4,
            stagger: 0.04,
            ease: 'power2.out',
            delay: 0.25,
          }
        )
      }
    }, containerRef.value)

    // Store context for cleanup
    activeGsapCtx = ctx
  })
})

onMounted(() => {
  // Initialize first item as active
  nextTick(() => {
    // Initial animations if needed
    // Assuming v-show handles the layout correctly on mount

    // Initialize timeline animation for first item
    animateTimeline(0)

    // Start scroll animation loop
    scrollAnimationFrame = requestAnimationFrame(handleScrollAnimation)
  })
})

onUnmounted(() => {
  if (scrollAnimationFrame) {
    cancelAnimationFrame(scrollAnimationFrame)
  }
})
</script>

<style scoped>
.journey-v2 {
  @apply relative w-full py-16 md:py-24 overflow-hidden;
  background: var(--color-deep-black);
  outline: none;
}

/* Header */
.journey-v2__header {
  @apply container mx-auto px-4 mb-12 flex items-end justify-between;
  will-change: transform, opacity, filter;
}

.journey-v2__header-left {
  @apply flex flex-col;
}

.journey-v2__label {
  @apply text-xs tracking-widest uppercase mb-2 block;
  font-family: monospace;
  color: var(--color-electric-purple);
}

.journey-v2__title {
  @apply text-4xl md:text-6xl font-black tracking-tighter;
  color: var(--color-text-primary);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

/* Accordion */
.journey-v2__accordion {
  @apply w-full flex flex-col md:flex-row px-2 md:px-4 gap-2 md:gap-3;
  height: auto;
}

@media (min-width: 768px) {
  .journey-v2__accordion {
    height: 570px;
  }
}

.journey-v2__card {
  @apply relative cursor-pointer;
  height: 100px; /* Mobile collapsed height */
  width: 100%; /* Ensure full width on mobile */
  flex: none; /* Mobile: Stacks vertically */
  background: transparent; /* Transparent so orb shows behind */
  border: none; /* Border moved to inner bg */
  /* Remove overflow hidden to allow orb to spill out */
  overflow: visible; 
  transition: flex 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  /* Performance optimizations */
  contain: layout style;
  will-change: flex;
  transform: translateZ(0);
}

/* Specific styling for the card background to act as the visual card */
.journey-v2__card-bg {
  @apply absolute inset-0 z-10;
  border-radius: 2rem; /* Explicitly requested radius */
  background: var(--color-surface);
  overflow: hidden; /* Clip inner content */
  corner-shape: squircle;
}

:root.dark .journey-v2__card-bg {
  background: var(--color-deep-black); /* Ensure opaque background */
}

:root.dark .journey-v2__card--active .journey-v2__card-bg {
  /* Dark mode specific adjustments if needed, otherwise uses generic rule above */
  box-shadow: 0 25px 60px -32px var(--orb-color-1);
}

/* Active state shadow (Light & Dark) */
.journey-v2__card--active .journey-v2__card-bg {
  /* Colorful shadow matching the card's theme */
  box-shadow: 0 25px 60px -32px var(--orb-color-1);
  /* Optional: Highlight border with color too, or keep it subtle? */
  border-color: var(--orb-color-1); 
}

.journey-v2__card--active {
  height: auto; /* Mobile expanded height */
  cursor: default;
}

@media (min-width: 768px) {
  .journey-v2__card {
    height: 100%;
    /* Desktop: Use the variable for flex ratio (4 vs 1) */
    flex: var(--card-flex) 1 0% !important;
  }
}

/* Define colors per index ON THE CARD so both orb and border can access */
.journey-v2__card:nth-child(1) { 
  --orb-color-1: rgba(124, 58, 237, 1);     /* Electric Purple */
  --orb-color-2: rgba(59, 130, 246, 0.6);   /* Blue */
}

.journey-v2__card:nth-child(2) { 
  --orb-color-1: rgba(59, 130, 246, 1);     /* Blue */
  --orb-color-2: rgba(16, 185, 129, 0.6);   /* Emerald */
}

.journey-v2__card:nth-child(3) { 
  --orb-color-1: rgba(236, 72, 153, 1);     /* Pink */
  --orb-color-2: rgba(124, 58, 237, 0.6);   /* Purple */
}

.journey-v2__card:nth-child(4) { 
  --orb-color-1: rgba(245, 158, 11, 1);     /* Amber */
  --orb-color-2: rgba(236, 72, 153, 0.6);   /* Pink */
}

/* Dynamic Border Glow */
.journey-v2__border-glow {
  @apply absolute inset-0 z-50 pointer-events-none;
  border-radius: 2rem;
  padding: 2.5px; /* Thicker border for visibility */
  
  corner-shape: squircle;
  /* The glow gradient following mouse */
  background: radial-gradient(
    400px circle at var(--orb-x) var(--orb-y), 
    var(--orb-color-1), 
    transparent 100%
  );
  
  /* Mask to show only the border area (padding area) */
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  
  opacity: 0;
  transition: opacity 0.3s ease;
}

.journey-v2__card:hover .journey-v2__border-glow {
  opacity: 1;
}

/* Active state border glow removed to avoid persistent border */
/* Only hover triggers the glow now */

/* Cleaned up obsolete color overrides */

/* Noise texture - only show on active cards for performance */
.journey-v2__noise {
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.journey-v2__card-bg--active .journey-v2__noise {
  opacity: 0.02;
}

/* Gradient Overlays */
.journey-v2__gradient {
  @apply absolute inset-0 z-10;
}

.journey-v2__gradient--base {
  background: linear-gradient(
    to top,
    rgba(255, 255, 255, 0.9),
    rgba(255, 255, 255, 0.4) 50%,
    transparent
  );
}

:root.dark .journey-v2__gradient--base {
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.4) 50%, transparent);
}

/* Vertical Text (Inactive State) */
.journey-v2__vertical {
  @apply absolute inset-0 z-20 flex items-center justify-center pointer-events-none transition-opacity duration-300;
}

.journey-v2__vertical-desktop {
  @apply hidden md:flex items-center gap-4 whitespace-nowrap;
  transform: rotate(-90deg);
}

.journey-v2__vertical-year {
  @apply text-2xl font-bold tracking-widest;
  color: var(--color-text-muted);
  transition: color 0.3s ease;
}

.journey-v2__vertical-line {
  @apply w-8 h-px;
  background: var(--color-border);
}

.journey-v2__vertical-role {
  @apply text-xs uppercase tracking-widest;
  font-family: monospace;
  color: var(--color-text-muted);
  opacity: 0.6;
}

.journey-v2__vertical-mobile {
  @apply flex md:hidden flex-col items-center justify-center h-full gap-1;
}

.journey-v2__vertical-year-mobile {
  @apply text-lg font-bold;
  color: var(--color-text-secondary);
}

.journey-v2__vertical-role-mobile {
  @apply text-[10px] uppercase tracking-widest text-center px-2;
  color: var(--color-text-muted);
}

/* Content Panel (Active State) */
.journey-v2__content {
  @apply flex relative p-6 md:p-12 z-30 flex-col w-full;
  height: auto;
  /* Display handled by Vue v-show */
}

@media (min-width: 768px) {
  .journey-v2__content {
    @apply absolute inset-0 justify-end h-full;
  }
}

.journey-v2__content-top {
  @apply mb-auto hidden md:flex justify-between items-start;
}

.journey-v2__status-badge {
  @apply flex items-center gap-2 px-3 py-1.5 rounded-full;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  /* Removed backdrop-filter for performance */
}

:root.dark .journey-v2__status-badge {
  background: rgba(30, 30, 35, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.journey-v2__status-badge span {
  @apply text-[10px] font-bold uppercase tracking-wider;
  color: var(--color-text-primary);
}

.journey-v2__status-dot {
  @apply w-1.5 h-1.5 rounded-full;
  background: #34d399;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Counter */
.journey-v2__counter {
  @apply flex items-baseline gap-1;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.journey-v2__counter-current {
  @apply text-3xl font-black;
  color: var(--color-text-primary);
}

.journey-v2__counter-separator {
  @apply text-lg font-light mx-1;
  color: var(--color-text-muted);
}

.journey-v2__counter-total {
  @apply text-lg font-medium;
  color: var(--color-text-muted);
}

/* Main Info */
.journey-v2__info {
  @apply space-y-4 md:space-y-6;
}

.journey-v2__meta {
  @apply flex flex-wrap items-center gap-x-4 gap-y-2 text-sm md:text-base;
}

.journey-v2__meta-date {
  @apply flex items-center gap-2 font-bold text-sm;
  color: var(--color-electric-purple);
}

.journey-v2__meta-separator {
  @apply hidden md:block w-1 h-1 rounded-full;
  background: var(--color-text-muted);
}

.journey-v2__meta-location {
  @apply flex items-center gap-2 text-sm;
  color: var(--color-text-secondary);
}

.journey-v2__title-block {
  @apply flex flex-col;
}

.journey-v2__company-name {
  @apply text-3xl md:text-3xl font-black leading-[0.95] mb-2;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: var(--color-text-primary);
}

.journey-v2__role {
  @apply text-xl md:text-xl font-light;
  color: var(--color-text-secondary);
}

/* Multi-role Timeline (Spine) */
.journey-v2__roles-timeline {
  @apply mt-4 pl-1 flex flex-col;
}

.journey-v2__sub-role {
  @apply flex gap-4 min-h-[60px]; /* Min height to allow line to stretch */
}

.journey-v2__sub-role-spine {
  @apply flex flex-col items-center pt-1.5;
  width: 20px;
}

.journey-v2__sub-role-dot {
  @apply w-3 h-3 rounded-full flex-shrink-0;
  background: var(--color-electric-purple);
  box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2);
}

.journey-v2__sub-role-line {
  @apply w-0.5 flex-grow my-1;
  background: var(--color-border);
  opacity: 0.5;
}

.journey-v2__sub-role-content {
  @apply flex flex-col pb-6;
}

.journey-v2__sub-role-title {
  @apply text-lg font-bold leading-tight mb-1;
  color: var(--color-text-primary);
}

.journey-v2__sub-role-meta {
  @apply text-sm flex flex-wrap gap-2;
  color: var(--color-text-secondary);
}

.journey-v2__sub-role-type {
  @apply opacity-70;
}

/* Multi-role Timeline (Spine) */
.journey-v2__roles-timeline {
  @apply mt-4 pl-1 flex flex-col;
}

.journey-v2__sub-role {
  @apply flex gap-4 min-h-[60px]; /* Min height to allow line to stretch */
}

.journey-v2__sub-role-spine {
  @apply flex flex-col items-center pt-1.5;
  width: 20px;
}

.journey-v2__sub-role-dot {
  @apply w-3 h-3 rounded-full flex-shrink-0;
  background: var(--color-electric-purple);
  box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2);
}

.journey-v2__sub-role-line {
  @apply w-0.5 flex-grow my-1;
  background: var(--color-border);
  opacity: 0.5;
}

.journey-v2__sub-role-content {
  @apply flex flex-col pb-4;
}

.journey-v2__sub-role-title {
  @apply text-lg font-bold leading-tight mb-1;
  color: var(--color-text-primary);
}

.journey-v2__sub-role-meta {
  @apply text-sm flex flex-wrap gap-2;
  color: var(--color-text-secondary);
}

.journey-v2__sub-role-type {
  @apply opacity-70;
}

.journey-v2__description-block {
  @apply relative;
}

.journey-v2__description {
  @apply text-sm md:text-base max-w-2xl leading-relaxed;
  color: var(--color-text-secondary);
}

/* Timeline Footer */
.timeline-footer {
  @apply hidden md:flex items-start w-full mt-8 px-4 md:px-8;
  max-width: 100%;
}

.timeline-footer__item {
  @apply flex flex-col items-center gap-3 relative z-10;
  flex-shrink: 0;
}

.timeline-footer__dot {
  @apply w-2 h-2 rounded-full;
  transition:
    background-color 0.3s ease,
    box-shadow 0.3s ease;
  will-change: transform;
}

.timeline-footer__dot--active {
  background-color: var(--color-electric-purple);
  box-shadow: 0 0 6px rgba(139, 92, 246, 0.4);
}

.timeline-footer__dot--inactive {
  background-color: var(--color-text-secondary);
  opacity: 0.3;
  box-shadow: none;
}

.timeline-footer__label {
  @apply text-xs font-normal tracking-wide;
  font-family: monospace;
  transition:
    color 0.3s ease,
    font-weight 0.3s ease;
  will-change: transform;
}

.timeline-footer__label--active {
  color: var(--color-text-secondary);
  opacity: 0.8;
}

.timeline-footer__label--inactive {
  color: var(--color-text-secondary);
  opacity: 0.3;
}

.timeline-footer__line-wrapper {
  @apply flex-grow mx-1 relative mt-1;
  height: 1px;
}

.timeline-footer__line-base {
  @apply absolute top-0 left-0 right-0;
  height: 1px;
  border-top: 1px dashed var(--color-border);
  opacity: 0.4;
}

.timeline-footer__line-active {
  @apply absolute top-0 left-0 right-0;
  height: 1px;
  background: linear-gradient(90deg, var(--color-electric-purple) 0%, rgba(139, 92, 246, 0.5) 100%);
  transform-origin: left center;
  transform: scaleX(0);
  opacity: 0.7;
}

/* Keyboard Hint */
.journey-v2__keyboard-hint {
  @apply hidden md:flex items-center justify-center gap-2 mt-8;
  color: var(--color-text-muted);
  font-size: 12px;
  font-family: monospace;
  opacity: 0.5;
  transition: opacity 0.3s ease;
}

.journey-v2:focus .journey-v2__keyboard-hint,
.journey-v2:hover .journey-v2__keyboard-hint {
  opacity: 1;
}

.journey-v2__keyboard-hint kbd {
  @apply px-2 py-1 rounded;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  font-size: 11px;
  min-width: 24px;
  text-align: center;
}

.journey-v2__social-link {
  @apply flex items-center gap-2 text-[var(--color-text-secondary)];
}
</style>
